import { CalcState, CalcStatus } from "../models/Models";
import { CompanyResponse, InsuranceCard } from "@/services/index.defs";
import { InsuranceService } from "@/services/InsuranceService";
import { InsuranceProviders } from "@/models/InsuranceProviders";
import { ActionContext } from "vuex";
import { CardService } from "@/services/CardService";
import { StatusChecker } from "@/utils/StatusChecker";

export class ICCalcState {
    cardId: string | number = null;
    buyTime: number = 180;
    buyTimer: any = null;
    lastCardJson: string = "";

    preCalcStates: CalcState[] = []; // precalcs
    calcStates: CalcState[] = []; // issue-ready calcs
    issue: CalcState = null; // paylink

    currentCalc: CalcState = new CalcState(); // selected pre calc

    percentageOfPassability: { icName: string; value: number }[] = [];
    checker: StatusChecker = null;
    forceEnd: boolean = false;
}

export default {
    namespaced: true as true,
    state: new ICCalcState(),
    mutations: {
        startBuyTimer(state: ICCalcState) {
            clearInterval(state.buyTimer);
            const specialBuyTime = {
                [InsuranceProviders.Soglasie]: 300,
                [InsuranceProviders.Renaissance]: 300,
            };
            state.buyTime = specialBuyTime[state.currentCalc?.insuranceProviderId] ?? 180;
            state.buyTimer = setInterval(() => {
                if (state.buyTime <= 0) {
                    clearInterval(state.buyTimer);
                }
                state.buyTime--;
            }, 1000);
        },
        endBuyTimer(state: ICCalcState) {
            state.buyTime = 180;
            clearInterval(state.buyTimer);
        },
        setInsuranceIssue(state: ICCalcState, resp: CalcState) {
            state.issue = resp;
        },
        addPrecalcResult(state: ICCalcState, payload: CalcState) {
            const stateIndex = state.preCalcStates.findIndex((state) => state.insuranceProviderId == payload.insuranceProviderId);
            if (stateIndex < 0) state.preCalcStates.push(payload);
            else state.preCalcStates.splice(stateIndex, 1, payload);
            state.preCalcStates = state.preCalcStates.sort((a, b) => {
                const getValue = (calc) => state.percentageOfPassability.find((x) => x.icName == calc.insuranceProviderId)?.value ?? 0;
                const valueA = getValue(a);
                const valueB = getValue(b);
                return +(valueA === null) - +(valueB === null) || +(valueA < valueB) || -(valueA > valueB); // o_o
            });
        },
        setCurrentCalc(state: ICCalcState, calc: CalcState) {
            state.currentCalc = calc;
        },
        setPercentageOfPassability(state: ICCalcState, data) {
            state.percentageOfPassability = data.sort((a, b) => {
                const valueA = a.value;
                const valueB = b.value;
                return +(valueA === null) - +(valueB === null) || +(valueA < valueB) || -(valueA > valueB); // >_>
            });
        },
        // v2
        startChecker(state: ICCalcState, data: { repeatableAction: Function; stopTrigger: Function; onTimeout: Function; onStop: Function }) {
            if (state.checker) state.checker.dispose(); // закрытие прошлого потока
            state.checker = new StatusChecker();
            state.checker.startStatusCheck(data.repeatableAction, data.stopTrigger, data.onStop, data.onTimeout, 15 * 60 * 1000); // запуск гуся 🦆
        },
        disposeChecker(state: ICCalcState) {
            state.checker?.dispose?.();
        },
        addCalcResult(state: ICCalcState, calc: any) {
            const byICName = (state: CalcState) => calc.insuranceProviderId == state.insuranceProviderId;
            const stateIndex = state.calcStates.findIndex(byICName);
            if (stateIndex < 0) state.calcStates.push(calc);
            else state.calcStates.splice(stateIndex, 1, calc);
            const byPrice = (a: CalcState, b: CalcState) => +(a.amount === null) - +(b.amount === null) || +(a.amount > b.amount) || -(a.amount < b.amount); // O_o
            state.calcStates = state.calcStates.sort(byPrice);
        },
    },
    actions: {
        async initCurrentCalc({ state, commit }, payload: { icname: string; cardId: string }) {
            const res = await CardService.getCalcState(payload.cardId, payload.icname);
            if (res.success) {
                commit("setCurrentCalc", res.data);
            }
        },

        async loadCalcs({ commit, state }, { card, providers, guid }: { card: InsuranceCard; providers: CompanyResponse[]; guid: string }) {
            providers.forEach(async (prov) => {
                try {
                    const res = await InsuranceService.calculateFast({ traceId: guid, body: card, icName: prov.code });
                    res.insuranceProviderId = prov.code;
                    commit("addPrecalcResult", res);
                } catch (e) {
                    console.error(e);
                    if (e.message.includes("504") || e.message.includes("timeout") || e.message.includes("Time-out")) {
                        const calc = state.preCalcStates.find((x) => x.insuranceProviderId == prov.code);
                        calc.status = CalcStatus.CalcFail;
                    }
                }
            });
        },
        async runPrecalc({ state, commit, dispatch }, { card, providers, guid }: { card: InsuranceCard; providers: CompanyResponse[]; guid: string }) {
            let res: { icName: string; value: number }[] = [];
            try {
                res = await InsuranceService.GetPercentageOfPassability(card.owner.address.kladrId);
                commit("setPercentageOfPassability", res);
            } catch (e) {}

            // generate empty calcs for each company
            // they will be replaced when recieved while polling
            providers.forEach((prov) => {
                const calc = new CalcState();
                calc.insuranceProviderId = prov.code;
                calc.status = CalcStatus.NeedToCalc;
                state.preCalcStates.push(calc);
            });

            state.cardId = guid; // is it needed?
            dispatch("loadCalcs", { card, providers, guid });
        },

        async pollCardIssuing({ state, commit, dispatch, rootGetters }, card: InsuranceCard) {
            state.forceEnd = false;
            commit("disposeChecker");
            console.log("start pollCardIssuing");
            const isFastCalcDisabled = rootGetters.isFastCalcDisabled;
            let errorCounter = 0;
            const issuingStopTrigger = async function(): Promise<boolean> {
                const calcStateLength = state.calcStates?.length;
                if (!calcStateLength)
                    return true;

                const active = (x) => [CalcStatus.NeedToCalc, CalcStatus.Processing].includes(x.status);
                const successful = (x) => [CalcStatus.Issued].includes(x.status);
                const shouldContinue = isFastCalcDisabled && !calcStateLength
                    ? true
                    : state.calcStates.some(active);

                if (!shouldContinue) {
                    const needForceEnd = isFastCalcDisabled && !calcStateLength
                        ? false
                        : !state.calcStates.some(successful);
                    if (needForceEnd)
                        state.forceEnd = true;

                    console.log("stop pollCardIssuing");
                }

                return shouldContinue;
            };
            const repeatableAction = async () => {
                try {
                    await dispatch("getCalcs", card.id);;
                } catch (error) {
                    errorCounter++
                    console.error(error);
                }
            }
            commit("startChecker", { repeatableAction, stopTrigger: issuingStopTrigger, onStop: null });
        },
        async getCalcs({ state, commit, dispatch }: any, cardId: string) {
            try {
                const response = await CardService.getCalcStates(cardId);
                if (response.success) {
                    response.data.forEach((calc) => dispatch("recieveCalcResult", calc));
                    const calc: CalcState = state.calcStates.find((calc: CalcState) => calc.insuranceProviderId == state.currentCalc?.insuranceProviderId);
                    if (calc?.status == CalcStatus.Issued) commit("setInsuranceIssue", calc);
                    return response.data;
                }

                throw new Error(response.error);
            } catch (error) {
                throw new Error(error);
            }
        },
        async recieveCalcResult({ state, commit }: ActionContext<ICCalcState, any>, calc: CalcState) {
            commit("addCalcResult", calc);
            const isCurrent = state.currentCalc?.insuranceProviderId == calc.insuranceProviderId;
            // const isSuccess = calc.status == CalcStatus.Issued;
            if (isCurrent) commit("setInsuranceIssue", calc); // Нужно только для аналитики?
        },
    },
    getters: {},
};
